import * as t from 'io-ts';

import {
  CompoundConditions,
  RCompoundConditions,
} from '../../settings/editors/conditions/compoundConditionModel';
import {PreExpression} from '../../settings/editors/expressions/expressionModel';
import {RuleAction} from '../../settings/rules/ruleActionModel';
import {RuleEventTypesEnum} from '../../settings/rules/ruleEventModel';
import {RuleTriggerEventTypes} from '../../settings/rules/ruleTriggerModel';
import {makeApiType, makeEnumRuntimeType} from './apiTypes';
import {RuleBundleTypesEnum} from './ruleCommonTypes';

/*
 * Runtime type.
 */

export {CompoundConditionTypesEnum as RuleCompoundConditionTypesEnum} from '../../settings/editors/conditions/compoundConditionModel';

export enum RuleComparatorTypesEnum {
  EQUALS = 'equals',
}

export const RRuleAction = t.dictionary(t.string, t.any);
export const RRuleExpression = t.dictionary(t.string, t.any);

export const RRuleBundleBase = t.intersection([
  t.type({
    id: t.union([t.Integer, t.undefined]),
    name: t.string,
    namespace: t.string,
    priority: t.Integer,
    sortOrder: t.union([t.Integer, t.undefined]),
    type: makeEnumRuntimeType(RuleBundleTypesEnum),
    url: t.union([t.string, t.undefined]),
  }),
  t.partial({
    dataFieldsTableId: t.union([t.null, t.number]),
  }),
]);

export const RRuleBase = t.readonly(
  t.intersection([
    t.type({
      id: t.union([t.Integer, t.undefined]),
      name: t.string,
      description: t.string,
      active: t.boolean,
      stopProcessing: t.boolean,
      events: t.readonlyArray(t.union([makeEnumRuntimeType(RuleEventTypesEnum), t.string])),
      compoundConditions: RCompoundConditions,
      expressions: t.union([t.readonlyArray(RRuleExpression), t.undefined]),
      actions: t.readonlyArray(RRuleAction),
      namespace: t.string,
      footprintNamespaces: t.readonlyArray(t.string),
      role: t.union([t.undefined, t.string]),
      sortOrder: t.union([t.Integer, t.undefined]),
      lastTriggeredAt: t.union([t.Integer, t.undefined]),
    }),
    t.partial({
      createdAt: t.union([t.Integer, t.undefined]),
    }),
  ]),
);

/*
 * Rule model depends if its fetched directly or within rule bundle.
 * When it's fetched directly, it carries rule bundle properties under
 * the `bundle` attribute. But when it's fetched from the rule bundle,
 * it doesnt. This is why we have a base model that is without the relationship.
 */

export const RRule = t.intersection([
  RRuleBase,
  t.readonly(
    t.type({
      bundle: RRuleBundleBase,
      // Override the typing since there is an issue with DeepPartial in the Redux state.
      compoundConditions: t.readonlyArray(t.any),
    }),
  ),
]);

const RRules = t.readonlyArray(RRule);

/*
 * Type.
 */

export type {
  CompoundConditions as RuleCompoundConditions,
  SimpleCompoundConditions,
} from '../../settings/editors/conditions/compoundConditionModel';

export interface RuleBaseModel extends t.TypeOf<typeof RRuleBase> {}
export interface RuleBaseModelStrict extends RuleBaseModel {
  actions: ReadonlyArray<RuleAction>;
  compoundConditions: CompoundConditions;
  events: ReadonlyArray<RuleTriggerEventTypes>;
  expressions: ReadonlyArray<PreExpression>;
}

export interface RuleModel extends t.TypeOf<typeof RRule> {}
export interface RuleModelStrict extends RuleModel {
  actions: ReadonlyArray<RuleAction>;
  compoundConditions: CompoundConditions;
  events: ReadonlyArray<RuleTriggerEventTypes>;
  expressions: ReadonlyArray<PreExpression>;
}

export type PartialRuleBaseModelStrict = Partial<RuleBaseModelStrict>;
export type PartialRuleModelStrict = Partial<RuleModelStrict>;

export enum RulesNamespaceTypesEnum {
  EVENTS = 'events',
  ACTIONS = 'actions',
  COMPOUND_CONDITIONS = 'compoundConditions',
}

/*
 * IO methods.
 */

export function parseRules(src: any) {
  return src.rules && src.rules.map(parseRule);
}

export function parseRule(src: any): RuleModel {
  return {
    actions: src.actions,
    active: src.active,
    bundle: {
      id: src.bundle.id,
      name: src.bundle.name,
      namespace: src.bundle.namespace,
      priority: src.bundle.priority,
      sortOrder: src.bundle.sort_order,
      type: src.bundle.type,
      url: src.bundle.url,
      dataFieldsTableId: src.bundle.data_fields_table_id,
    },
    compoundConditions: src.compound_conditions,
    expressions: src.expressions,
    events: src.events,
    id: src.id,
    name: src.name,
    description: src.description,
    namespace: src.namespace,
    footprintNamespaces: src.footprint_namespaces,
    role: src.role,
    stopProcessing: src.stop_processing,
    sortOrder: src.sort_order,
    lastTriggeredAt: src.last_triggered_at || undefined,
    createdAt: src.created_at || undefined,
  };
}

export function serializeRule(src: RuleBaseModel): any {
  return {
    actions: src.actions,
    active: src.active,
    compound_conditions: src.compoundConditions,
    expressions: src.expressions,
    events: src.events,
    id: src.id,
    name: src.name,
    namespace: src.namespace,
    role: src.role,
    stop_processing: src.stopProcessing,
    sort_order: src.sortOrder,
  };
}

/*
 * API type.
 */

export const ApiRule = makeApiType('rule', RRule, parseRule);
export const ApiRules = makeApiType('rules', RRules, parseRules);
